<template>
  <input
    ref="checkbox"
    type="checkbox"
    class="review-selection-header-cell"
    :checked="allSelected"
    :disabled="disabled"
    :aria-label="label"
    :data-testid="testId"
    @change="handleToggleAll"
  />
</template>

<script setup>
/**
 * Select-all checkbox content for a review table's selection column header.
 * Renders the bare checkbox — the owning table supplies the <th>. Indeterminate
 * is a DOM property (not an attribute), so it is applied through the template
 * ref whenever the prop changes.
 */
import { useTemplateRef, watchEffect } from "vue";

const props = defineProps({
  /** Whether every selectable row is currently selected. */
  allSelected: {
    type: Boolean,
    default: false,
  },
  /** Mixed state: some but not all rows selected. */
  indeterminate: {
    type: Boolean,
    default: false,
  },
  /**
   * Set when no row is selectable, so a click cannot toggle the native checked state
   * while the selection stays empty.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Accessible name for the checkbox. */
  label: {
    type: String,
    default: "",
  },
  /**
   * data-testid from the caller's flow catalog. Shared component: never
   * hardcoded here.
   */
  testId: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["toggle-all"]);

function handleToggleAll() {
  emit("toggle-all");
}

const checkbox = useTemplateRef("checkbox");

function applyIndeterminate() {
  if (checkbox.value) {
    checkbox.value.indeterminate = props.indeterminate;
  }
}

watchEffect(applyIndeterminate, { flush: "post" });
</script>

<style scoped lang="scss">
.review-selection-header-cell {
  cursor: pointer;
  margin: 0;
  vertical-align: middle;
  accent-color: var(--sp-sys-color-primary);

  &:disabled {
    cursor: not-allowed;
  }
}
</style>
